import React, { useEffect, useMemo, useState } from 'react';
import {
  KendoReactGrid,
  GridColumn,
  SortDescriptor
} from '@Shared.Angular/kendo.react/KendoReactGrid';
import {
  GridCustomCellProps,
  GridCustomFilterCellProps
} from '@progress/kendo-react-grid';
import { HeaderTdElement } from '@progress/kendo-react-data-tools';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { getService } from '@Client/runner.hooks/useService';
import IFormThemeOverview from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeOverview';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import ActionButtons from '@Client/runner.library/ActionButtons';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import StatusBadge from '@Client/runner.library/StatusBadge';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import AutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/AutoCompleteFilter';
import { DateFilter } from '@Shared.Angular/flowingly.components/Filters/DateFilter/DateFilter';
import DeleteModal from '@Shared.Angular/flowingly.components/DeleteModal/DeleteModal';
import { getFormThemeLastUpdatedDate } from '@Shared.Angular/flowingly.services/data/formThemeDateUtils';
import GridAutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/GridAutoCompleteFilter';

type LibraryFormThemeRow = IFormThemeOverview & {
  status: string;
  lastUpdatedLocal: Date | null;
  hasUnpublishedChanges: boolean;
};

type FilterChangeArgs = Parameters<
  NonNullable<GridCustomFilterCellProps['onChange']>
>[0];

const PAGE_SIZE = 15;
const SORT_FIELD_IS_DEFAULT_THEME = 'isDefaultTheme';
const SORT_FIELD_LAST_UPDATED = 'lastUpdatedLocal';

export default function LibraryFormThemesGrid() {
  const formThemesApiService = getService('formThemesApiService');
  const permissionsService = getService('permissionsService');
  const notificationService = getService('notificationService');
  const $state = getService('$state');
  const moment = getService('momentService');
  const tempModelerUrlService = getService('tempModelerUrlService');

  const [formThemes, setFormThemes] = useState<IFormThemeOverview[]>([]);
  const [gridItemsLoaded, setGridItemsLoaded] = useState(false);
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: []
  });
  const [deleteModalTarget, setDeleteModalTarget] =
    useState<LibraryFormThemeRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canCreateFormThemes = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.FORM_THEME_CREATE
  );
  const canDeleteFormThemes = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.FORM_THEME_DELETE
  );

  const loadFormThemes = (forceRefresh = false) => {
    setGridItemsLoaded(false);
    formThemesApiService
      .getFormThemes(forceRefresh)
      .then((themes: IFormThemeOverview[]) => {
        setFormThemes(themes ?? []);
      })
      .catch(() => {
        notificationService.showErrorToast(
          'Unable to load form themes. Please try again.'
        );
        setFormThemes([]);
      })
      .finally(() => {
        setGridItemsLoaded(true);
      });
  };

  useEffect(() => {
    loadFormThemes(true);
  }, []);

  const gridData = useMemo<LibraryFormThemeRow[]>(() => {
    return formThemes.reduce<LibraryFormThemeRow[]>((items, theme) => {
      const resolvedStatus = theme.status && theme.status.trim();
      const normalizedStatus = resolvedStatus.toLowerCase();
      if (normalizedStatus !== 'published') {
        return items;
      }

      const lastUpdatedLocal = getFormThemeLastUpdatedDate(theme);
      items.push({
        ...theme,
        status: resolvedStatus,
        lastUpdatedLocal,
        createdBy: theme.createdBy,
        description: theme.description || '',
        hasUnpublishedChanges: !!theme.isDraftVersionAvailable
      });

      return items;
    }, []);
  }, [formThemes]);

  const namesMemo = useMemo(
    () =>
      Array.from(new Set<string>(gridData.map((item) => item.name))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [gridData]
  );

  const creatorsMemo = useMemo(
    () =>
      Array.from(new Set<string>(gridData.map((item) => item.createdBy || '')))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [gridData]
  );

  const statusesMemo = useMemo(
    () =>
      Array.from(new Set<string>(gridData.map((item) => item.status))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [gridData]
  );

  const ellipsisCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field as keyof LibraryFormThemeRow];
    const textValue = value ?? '';
    return (
      <td {...props.tdProps} title={String(textValue)}>
        {textValue as React.ReactNode}
      </td>
    );
  };

  const nameCell = (props: GridCustomCellProps) => {
    const theme = props.dataItem as LibraryFormThemeRow;
    const value = theme.name;
    const unpublishedMessage = 'This theme has unpublished changes';
    return (
      <td
        {...props.tdProps}
        title={
          theme.hasUnpublishedChanges
            ? `${value} - ${unpublishedMessage}`
            : value
        }
      >
        {value}
        {theme.hasUnpublishedChanges && (
          <div className="unpublished-changes-message secondary-text">
            {unpublishedMessage}
          </div>
        )}
      </td>
    );
  };

  const statusCell = (props: GridCustomCellProps) => {
    const status = (props.dataItem as LibraryFormThemeRow).status;
    return (
      <td {...props.tdProps}>
        <StatusBadge status={status} />
      </td>
    );
  };

  const dateCell = (props: GridCustomCellProps) => {
    const value = (props.dataItem as LibraryFormThemeRow).lastUpdatedLocal;
    if (!value) {
      return <td {...props.tdProps}></td>;
    }

    return (
      <td
        {...props.tdProps}
        title={moment(value).format('YYYY-MM-DD HH:mm:ss')}
      >
        {moment(value).format('DD/MM/YYYY')}
      </td>
    );
  };

  const handleEdit = (theme: LibraryFormThemeRow) => {
    $state.go(STATE_MAPPINGS.libraryFormTheme, {
      formThemeId: theme.id
    });
  };

  const handleCopy = (theme: LibraryFormThemeRow) => {
    const copyUrl = `/library/formthemes/${theme.id}?copy=true`;
    tempModelerUrlService.openLinkInNewTab(copyUrl);
  };

  const nameFilterCell: React.FC<GridCustomFilterCellProps> = (props) => (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ''}
      {...props.thProps}
    >
      <AutoCompleteFilter
        {...props}
        data={namesMemo}
        placeholder="Search by Name"
      />
    </HeaderTdElement>
  );

  const creatorFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, creatorsMemo, 'Search by Creator'),
    [creatorsMemo]
  );

  const statusFilterCell: React.FC<GridCustomFilterCellProps> = (props) => (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ''}
      {...props.thProps}
    >
      <AutoCompleteFilter
        {...props}
        data={statusesMemo}
        placeholder="Search by Status"
        onChange={(event) => {
          if (event.value) {
            if (props.onChange) {
              const change: FilterChangeArgs = {
                operator: 'eq',
                value: event.value,
                syntheticEvent: event.syntheticEvent
              };
              props.onChange(change);
            }
          } else {
            if (props.onChange) {
              const change: FilterChangeArgs = {
                value: null,
                operator: '',
                syntheticEvent: event.syntheticEvent
              };
              props.onChange(change);
            }
          }
        }}
      />
    </HeaderTdElement>
  );

  const dateFilterCell: React.FC<GridCustomFilterCellProps> = (props) => (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ''}
      {...props.thProps}
    >
      <DateFilter
        value={props.value as Date | null}
        onChange={(args) =>
          props.onChange?.({
            value: args.value,
            operator: args.operator,
            syntheticEvent: args.syntheticEvent
          })
        }
      />
    </HeaderTdElement>
  );

  const closeDeleteModal = () => {
    setDeleteModalTarget(null);
    setIsDeleting(false);
  };

  const handleDelete = () => {
    if (!deleteModalTarget || isDeleting) {
      return;
    }

    const themeToDelete = deleteModalTarget;
    setIsDeleting(true);

    formThemesApiService
      .deleteFormTheme(themeToDelete.id)
      .then(() => {
        notificationService.showSuccessToast(`${themeToDelete.name} deleted.`);
        setFormThemes((items) =>
          items.filter((item) => item.id !== themeToDelete.id)
        );
        setDeleteModalTarget(null);
      })
      .catch(() => {
        notificationService.showErrorToast(
          'Failed to delete form theme. Please try again.'
        );
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const generateActionsCell = (props: GridCustomCellProps) => {
    const theme = props.dataItem as LibraryFormThemeRow;

    const editHandler = canCreateFormThemes
      ? () => handleEdit(theme)
      : undefined;
    const deleteHandler =
      canDeleteFormThemes && !theme.isDefaultTheme
        ? () => {
            setDeleteModalTarget(theme);
            setIsDeleting(false);
          }
        : undefined;
    const copyHandler = canCreateFormThemes
      ? () => handleCopy(theme)
      : undefined;

    return (
      <td {...props.tdProps}>
        <ActionButtons
          showButtons={{ edit: true, copy: true, delete: true }}
          onEditClick={editHandler}
          onDeleteClick={deleteHandler}
          onCopyClick={copyHandler}
        />
      </td>
    );
  };

  const initialSort = useMemo(
    () => [
      { field: SORT_FIELD_IS_DEFAULT_THEME, dir: 'desc' } as SortDescriptor,
      { field: SORT_FIELD_LAST_UPDATED, dir: 'desc' } as SortDescriptor
    ],
    []
  );

  const emptyStateBody = 'Get started and create your first Form Theme.';

  return (
    (!gridItemsLoaded && <div></div>) ||
    (gridItemsLoaded && gridData.length > 0 && (
      <div className="library-grid">
        <KendoReactGrid
          id="library-form-themes-grid"
          autoProcessData={{
            filter: true,
            search: true,
            sort: true,
            group: false,
            page: true
          }}
          data={gridData}
          dataItemKey="id"
          defaultSort={initialSort}
          defaultTake={PAGE_SIZE}
          filter={filter}
          filterable={true}
          onFilterChange={(event) =>
            setFilter(
              event.filter ?? {
                logic: 'and',
                filters: []
              }
            )
          }
          pageable={{
            buttonCount: 10,
            info: true,
            previousNext: true
          }}
          sortable={true}
          resizable={true}
        >
          <GridColumn
            field="name"
            title="Name"
            className="column-name"
            cells={{ data: nameCell, filterCell: nameFilterCell }}
          />
          <GridColumn
            field="description"
            title="Description"
            className="column-description ellipsis-column"
            filterable={false}
            cells={{ data: ellipsisCell }}
          />
          <GridColumn
            field="createdBy"
            title="Created By"
            className="column-created-by ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: creatorFilterCellMemo }}
          />
          <GridColumn
            field="lastUpdatedLocal"
            title="Last Updated"
            filter="date"
            className="column-last-updated"
            cells={{ data: dateCell, filterCell: dateFilterCell }}
          />
          <GridColumn
            field="status"
            title="Status"
            className="column-status"
            cells={{ data: statusCell, filterCell: statusFilterCell }}
          />
          <GridColumn
            title="Actions"
            sortable={false}
            filterable={false}
            resizable={false}
            width={200}
            className="column-actions"
            cells={{ data: generateActionsCell }}
          />
        </KendoReactGrid>
        {deleteModalTarget && (
          <DeleteModal
            id="form-theme-delete-modal"
            title="Delete Form Theme"
            subject={deleteModalTarget.name}
            closeModal={closeDeleteModal}
            deleteAction={!isDeleting ? handleDelete : undefined}
          />
        )}
      </div>
    )) ||
    (gridItemsLoaded && gridData.length === 0 && (
      <EmptyState
        summaryText="Build your Form Theme library"
        bodyText={emptyStateBody}
      />
    ))
  );
}
